'use client';

import { useUIStore } from '@/store/uiStore';
import { useEffect } from 'react';

/**
 * UI 관련 커스텀 훅
 * - 사이드바 상태 관리
 * - 테마 설정
 * - 모달 관리
 * - 알림 시스템
 */
export function useUI() {
  const {
    sidebarState,
    isSidebarOpen,
    theme,
    isMobile,
    isModalOpen,
    modalContent,
    notifications,
    toggleSidebar,
    setSidebarState,
    setTheme,
    setMobile,
    openModal,
    closeModal,
    addNotification,
    removeNotification,
    clearNotifications,
  } = useUIStore();

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth < 768;
      setMobile(isMobileView);
    };

    // 초기 설정
    handleResize();

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setMobile]);

  // 테마 변경 시 HTML 속성 업데이트
  useEffect(() => {
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches 
        ? 'dark' 
        : 'light';
      document.documentElement.setAttribute('data-theme', systemTheme);
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);

  // 시스템 테마 변경 감지
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        const systemTheme = mediaQuery.matches ? 'dark' : 'light';
        document.documentElement.setAttribute('data-theme', systemTheme);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  return {
    // 사이드바 상태
    sidebarState,
    isSidebarOpen,
    toggleSidebar,
    setSidebarState,

    // 테마
    theme,
    setTheme,

    // 모바일
    isMobile,

    // 모달
    isModalOpen,
    modalContent,
    openModal,
    closeModal,

    // 알림
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,

    // 편의 함수들
    showSuccess: (title: string, message?: string) =>
      addNotification({ type: 'success', message: message || title }),
    showError: (title: string, message?: string) =>
      addNotification({ type: 'error', message: message || title }),
    showWarning: (title: string, message?: string) =>
      addNotification({ type: 'warning', message: message || title }),
    showInfo: (title: string, message?: string) =>
      addNotification({ type: 'info', message: message || title }),
  };
}
